import { createSlice } from '@reduxjs/toolkit';
import { ProspectObject } from '../../params/prospect';
import { ProspectState } from '../../params/redux/prospect';
import { createManualProspectThunk, deleteProspectItemThunk, deleteProspectThunk, fetchProspectThunk, fetchSuggestionsThunk, getProspectDetailThunk, getProspectsThunk, pushProspectToPipelineThunk, searchProspectsThunk, uploadManualProspectThunk } from '../thunks/prospect';
// 1751299782406
// 1751299782986
// 1751299784379
// Initial state with default values for authentication and user info
const initialState: ProspectState = {
  prospects: [],
  selectedProspect: null,
  prospectDetail: null,
  suggestions: [],
  cities: [],
  zips: [],
  categories: [],
  manual: {
    data: {
      user_id: 0,
      niche: '',
      city: '',
      state: '',
      country: '',
      file: '',
      temp_token: '',
      processed_rows: 0,
      status: 'pending',
      created_at: '',
      updated_at: '',
      id: 0
    },
    isManual: false,
    csvHeaders: [],
    rows: [],
    mapping: {}
  },
  loaders: {
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isSearching: false,
    isInserting: false
  }
};

// Slice for app-related states and actions
const prospectSlice = createSlice({
  // Slice name
  name: 'prospect',
  // inital state for the slice
  initialState,
  reducers: {
    settingSelectedProspect: (state, action) => {
      state.selectedProspect = action.payload;
    },
    settingProspectDetail: (state, action) => {
      state.prospectDetail = action.payload || null;
    },
    settingProspectsManualImport: (state, action) => {
      const { isManual, headers, rows } = action.payload;
      state.manual.isManual = isManual;
      state.manual.csvHeaders = headers;
      state.manual.rows = rows;
    },
    settingProspectsManualMapping: (state, action) => {
      state.manual.mapping = action.payload;
    },
    // Resets loading states related to app actions
    resettingProspectLoader: (state) => {
      state.loaders = initialState.loaders;
    },
    // Resets all app data to the initial state
    resettingProspectData: () => initialState
  },
  extraReducers: (builder) => {
    // Handle async actions for login
    builder
      .addCase(searchProspectsThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isSearching = true;
      })
      .addCase(searchProspectsThunk.fulfilled, () => {
        //
      })
      .addCase(searchProspectsThunk.rejected, () => {
        //
      });
    builder
      .addCase(getProspectsThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isLoading = true;
        state.prospects = [];
      })
      .addCase(getProspectsThunk.fulfilled, (state, action) => {
        state.prospects = action.payload as ProspectObject[];
      })
      .addCase(getProspectsThunk.rejected, (state) => {
        state.prospects = [];
      });
    builder
      .addCase(pushProspectToPipelineThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isInserting = true;
      })
      .addCase(pushProspectToPipelineThunk.fulfilled, () => {
        //
      })
      .addCase(pushProspectToPipelineThunk.rejected, () => {
        //
      });
    builder
      .addCase(getProspectDetailThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isLoading = true;
        state.prospectDetail = null;
      })
      .addCase(getProspectDetailThunk.fulfilled, (state, action) => {
        const prospectDetail = action.payload as ProspectObject;
        state.prospectDetail = prospectDetail;

        // 🛠 Extract all cities from results
        const citiesSet = new Set<string>();
        const zipSet = new Set<string>();
        const categorySet = new Set<string>();

        prospectDetail.results.forEach((deal) => {
          if (deal.meta) {
            deal.meta.forEach((metaItem) => {
              if (metaItem.meta_key === 'address2') {
                citiesSet.add(metaItem.meta_value.trim());
              }
              if (metaItem.meta_key === 'zip') {
                zipSet.add(metaItem.meta_value.trim());
              }
              if (metaItem.meta_key === 'category') {
                categorySet.add(metaItem.meta_value.toLowerCase().trim());
              }
            });
          }
        });

        // 🆕 Save unique cities array
        state.cities = Array.from(citiesSet).sort();
        state.zips = Array.from(zipSet).sort();
        state.categories = Array.from(categorySet).sort();
      })
      .addCase(getProspectDetailThunk.rejected, (state) => {
        state.prospectDetail = null;
      });
    builder
      .addCase(fetchSuggestionsThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isSearching = true;
      })
      .addCase(fetchSuggestionsThunk.fulfilled, (state, action) => {
        state.suggestions = action.payload as string[];
      })
      .addCase(fetchSuggestionsThunk.rejected, (state) => {
        state.suggestions = [];
      });
    builder
      .addCase(deleteProspectThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isDeleting = true;
      })
      .addCase(deleteProspectThunk.fulfilled, () => {
        //
      })
      .addCase(deleteProspectThunk.rejected, () => {
        //
      });
    builder
      .addCase(deleteProspectItemThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isDeleting = true;
      })
      .addCase(deleteProspectItemThunk.fulfilled, () => {
        //
      })
      .addCase(deleteProspectItemThunk.rejected, () => {
        //
      });
    builder
      .addCase(fetchProspectThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isLoading = true;
      })
      .addCase(fetchProspectThunk.fulfilled, () => {
        //
      })
      .addCase(fetchProspectThunk.rejected, () => {
        //
      });
    builder
      .addCase(uploadManualProspectThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isCreating = true;
      })
      .addCase(uploadManualProspectThunk.fulfilled, (state, action) => {
        const { data, first11Rows } = action.payload as {
          data: {
            user_id: number;
            niche: string;
            city: string;
            state: string;
            country: string;
            file: string;
            temp_token: string;
            processed_rows: number;
            status: 'pending' | 'active',
            updated_at: string;
            created_at: string;
            id: number;
          };
          first11Rows: string[][];
        };
        const [csvHeaders] = first11Rows;
        const rows = first11Rows.slice(1).map((row) => Object.fromEntries(csvHeaders.map((header, index) => [header, row[index] || ''])));
        state.manual.isManual = true;
        state.manual.rows = rows;
        state.manual.csvHeaders = csvHeaders;
        state.manual.data = data;
        state.manual.mapping = {};
      })
      .addCase(uploadManualProspectThunk.rejected, (state) => {
        state.manual.isManual = false;
        state.manual.rows = [];
        state.manual.csvHeaders = [];
        state.manual.data = initialState.manual.data;
        state.manual.mapping = {};
      });
    builder
      .addCase(createManualProspectThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isCreating = true;
      })
      .addCase(createManualProspectThunk.fulfilled, (state) => {
        state.manual.isManual = false;
        state.manual.rows = [];
        state.manual.csvHeaders = [];
        state.manual.data = initialState.manual.data;
      })
      .addCase(createManualProspectThunk.rejected, () => {
        //
      });
  }
});

// Exporting actions for updating app state
export const {
  settingProspectsManualMapping,
  settingProspectsManualImport,
  settingSelectedProspect,
  settingProspectDetail,
  resettingProspectData,
  resettingProspectLoader
} = prospectSlice.actions;

// Exporting the reducer to be used in the store
export default prospectSlice.reducer;
